// src/pages/locations/components/CountryPicker.tsx
import React from "react";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";

import type { LocationCountry } from "@/api/features/locations/types";

import { controlClass } from "./form/controlClass";
import { countryFlag } from "./countryFlag";

type Props = {
  id: string;
  value: string;
  onChange: (iso2: string) => void;
  countries: LocationCountry[];
  disabled?: boolean;
  error?: string | null;
  describedBy?: string;
  placeholder?: string;
  /** "field" sits in a form; "filter" is the compact toolbar version. */
  variant?: "field" | "filter";
  /** Offer an × to clear the selection (toolbar filters). */
  allowClear?: boolean;
};

/**
 * Type-ahead country picker used both in the forms and as the list filter, so
 * choosing a country works the same way everywhere: type "ind" or "in" to
 * narrow, arrow keys to move, Enter to pick, Esc to close.
 */
export const CountryPicker: React.FC<Props> = ({
  id,
  value,
  onChange,
  countries,
  disabled = false,
  error,
  describedBy,
  placeholder = "Search a country…",
  variant = "field",
  allowClear = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const selected = React.useMemo(
    () => countries.find((c) => c.iso2 === value) ?? null,
    [countries, value]
  );

  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;

    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.iso2.toLowerCase().startsWith(q)
    );
  }, [countries, query]);

  // Close when a click lands outside the widget.
  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Keep the highlighted row in view while arrowing through the list.
  React.useEffect(() => {
    if (!open) return;
    const active = listRef.current?.children[activeIndex] as
      | HTMLElement
      | undefined;
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const openPicker = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setActiveIndex(Math.max(0, countries.findIndex((c) => c.iso2 === value)));
  };

  const select = (iso2: string) => {
    onChange(iso2);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openPicker();
        return;
      }
      setActiveIndex((i) => {
        if (matches.length === 0) return 0;
        const next = event.key === "ArrowDown" ? i + 1 : i - 1;
        return (next + matches.length) % matches.length;
      });
      return;
    }

    if (event.key === "Enter" && open) {
      event.preventDefault();
      const choice = matches[activeIndex];
      if (choice) select(choice.iso2);
      return;
    }

    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      setQuery("");
    }
  };

  const isFilter = variant === "filter";
  const flag = selected ? countryFlag(selected.iso2) : "";
  const showClear = allowClear && Boolean(selected) && !open && !disabled;

  const displayValue = open ? query : selected ? selected.name : "";

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 select-none text-gray-400">
          {!open && flag ? (
            <span className="text-base leading-none">{flag}</span>
          ) : (
            <FiSearch />
          )}
        </span>

        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={displayValue}
          placeholder={open && selected ? selected.name : placeholder}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          onFocus={openPicker}
          onClick={openPicker}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onKeyDown={onKeyDown}
          // Right padding has to clear whatever is stacked on that edge:
          // chevron, then the ISO badge, then the clear button.
          className={controlClass(
            error,
            `pl-9 ${
              showClear ? "pr-24" : !open && selected ? "pr-16" : "pr-9"
            } ${isFilter ? "h-[38px]" : ""}`
          )}
        />

        {/* The code stays visible while closed — it is what the data keys on. */}
        {!open && selected && (
          <span
            className={`pointer-events-none absolute top-1/2 -translate-y-1/2 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 ${
              showClear ? "right-14" : "right-8"
            }`}
          >
            {selected.iso2}
          </span>
        )}

        {showClear && (
          <button
            type="button"
            onClick={() => {
              onChange("");
              inputRef.current?.focus();
            }}
            aria-label="Clear country"
            className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <FiX />
          </button>
        )}

        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
          <FiChevronDown />
        </span>
      </div>

      {open && (
        <ul
          ref={listRef}
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full min-w-[240px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {matches.length === 0 && (
            <li className="px-3 py-3 text-sm text-gray-500">
              No country matches “{query}”.
            </li>
          )}

          {matches.map((country, index) => {
            const isActive = index === activeIndex;
            const isSelected = country.iso2 === value;

            return (
              <li
                key={country.iso2}
                role="option"
                aria-selected={isSelected}
                // mousedown fires before blur, so the click always registers.
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(country.iso2);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm ${
                  isActive ? "bg-sky-50 text-sky-900" : "text-gray-700"
                }`}
              >
                <span className="w-5 shrink-0 text-base leading-none">
                  {countryFlag(country.iso2)}
                </span>
                <span className={`flex-1 ${isSelected ? "font-semibold" : ""}`}>
                  {country.name}
                </span>
                <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-500">
                  {country.iso2}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
